import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Search, Plus, RefreshCcw, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, PieChart, Layers, Clock, Target, Award, MessageSquare, Wallet, CreditCard, Calendar, Tag, Trash2, Shield } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import FacturationListPageLayout, { facturationTableScrollClass } from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { mapAchatsStatCards, buildAchatsImportExportProps, buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const NS = 'modules.achats.pages.budgetAchatsPage';

const BudgetAchatsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.budgetAchatsPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);
  const dateLocale = getBrandLocale(i18n.language);
  const currencySuffix = getBrandCurrencySuffix();
  const [searchTerm, setSearchTerm] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBudget, setViewingBudget] = useState(null);
  
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'financial', label: p('section_amounts_finance'), icon: DollarSign, iconColor: 'text-green-400' },
    { id: 'notes', label: p('section_notes'), icon: MessageSquare, iconColor: 'text-amber-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    budget_reference: '', 
    budget_name: '',
    fiscal_year: '',
    period_start: new Date().toISOString().split('T')[0],
    period_end: '',
    budget_type: 'annual',
    status: 'draft',
    department: '',
    category: '',
    priority: 'medium',
    allocated_amount: '',
    spent_amount: '',
    committed_amount: '',
    available_amount: '',
    budget_threshold: '',
    alert_percentage: '',
    approved_by: '',
    approval_date: '',
    responsible_person: '',
    cost_center: '',
    project_code: '',
    variance_amount: '',
    variance_percentage: '',
    notes: '',
    justification: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_budgets');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadBudgets(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBudgets = async () => {
    try {
      const response = await localApiAdapter.get('/achats/budget');
      setBudgets(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading budgets:', error);
      setBudgets([]);
    }
  };

  const stats = {
    total: budgets.length,
    draft: budgets.filter(b => b.status === 'draft').length,
    approved: budgets.filter(b => b.status === 'approved').length,
    active: budgets.filter(b => b.status === 'active').length,
    totalAllocated: budgets.reduce((sum, b) => sum + (parseFloat(b.allocated_amount) || 0), 0),
    totalSpent: budgets.reduce((sum, b) => sum + (parseFloat(b.spent_amount) || 0), 0),
    totalAvailable: budgets.reduce((sum, b) => sum + (parseFloat(b.available_amount) || 0), 0),
    avgUtilization: budgets.length > 0 ? (budgets.reduce((sum, b) => {
      const allocated = parseFloat(b.allocated_amount) || 0;
      const spent = parseFloat(b.spent_amount) || 0;
      return sum + (allocated > 0 ? (spent / allocated * 100) : 0);
    }, 0) / budgets.length).toFixed(1) : 0
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_budgets', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_type', name: t('common.by_type'), icon: PieChart },
    { id: 'par_statut', name: t('common.by_status'), icon: Target }
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'draft', name: p('dist_draft'), icon: FileText },
    { id: 'approved', name: p('quick_approved'), icon: CheckCircle },
    { id: 'active', name: p('dist_active'), icon: TrendingUp }
  ]), [t, p]);

  const budgetDistribution = useMemo(() => ({
    all_budgets: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'draft', name: p('dist_draft'), count: stats.draft, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { filterKey: 'annual', name: p('type_annual'), count: budgets.filter(b => b.budget_type === 'annual').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'quarterly', name: p('type_quarterly'), count: budgets.filter(b => b.budget_type === 'quarterly').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'monthly', name: p('type_monthly'), count: budgets.filter(b => b.budget_type === 'monthly').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'project', name: p('type_project'), count: budgets.filter(b => b.budget_type === 'project').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { filterKey: 'draft', name: p('dist_draft'), count: stats.draft, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'approved', name: p('dist_approved'), count: stats.approved, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'closed', name: p('dist_closed'), count: budgets.filter(b => b.status === 'closed').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  }), [budgets, stats, p]);

  const distributionInfo = budgetDistribution[mainViewMode] || budgetDistribution.all_budgets;

  const baseFilteredBudgets = budgets.filter(b => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const budgetDate = new Date(b.period_start);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (budgetDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (budgetDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !b.budget_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !b.budget_reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (qawaem && statusFilter !== 'all' && b.status !== statusFilter) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_budgets') {
      if (quickFilter === 'draft' && b.status !== 'draft') return false;
      if (quickFilter === 'approved' && b.status !== 'approved') return false;
      if (quickFilter === 'active' && b.status !== 'active') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_budgets':
          if (activeDistributionFilter === 'draft' && b.status !== 'draft') return false;
          if (activeDistributionFilter === 'approved' && b.status !== 'approved') return false;
          if (activeDistributionFilter === 'active' && b.status !== 'active') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'annual' && b.budget_type !== 'annual') return false;
          if (activeDistributionFilter === 'quarterly' && b.budget_type !== 'quarterly') return false;
          if (activeDistributionFilter === 'monthly' && b.budget_type !== 'monthly') return false;
          if (activeDistributionFilter === 'project' && b.budget_type !== 'project') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'draft' && b.status !== 'draft') return false;
          if (activeDistributionFilter === 'approved' && b.status !== 'approved') return false;
          if (activeDistributionFilter === 'active' && b.status !== 'active') return false;
          if (activeDistributionFilter === 'closed' && b.status !== 'closed') return false;
          break;
      }
    }
    
    return true;
  });

  const generateBudgetReference = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'purchaseBudget', 'BUD', budgets, (b) => b.budget_reference),
    [achatsSettings, budgets],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const budgetReference = resolveAchatsEntityReference(
        achatsSettings,
        'purchaseBudget',
        'BUD',
        budgets,
        (b) => b.budget_reference,
        formData.budget_reference,
      );
      const submitData = { 
        budget_reference: budgetReference,
        budget_name: formData.budget_name,
        fiscal_year: formData.fiscal_year,
        period_start: formData.period_start,
        period_end: formData.period_end,
        budget_type: formData.budget_type,
        status: formData.status,
        department: formData.department,
        category: formData.category,
        priority: formData.priority,
        allocated_amount: parseFloat(formData.allocated_amount) || 0,
        spent_amount: parseFloat(formData.spent_amount) || 0,
        committed_amount: parseFloat(formData.committed_amount) || 0,
        available_amount: parseFloat(formData.available_amount) || 0,
        budget_threshold: parseFloat(formData.budget_threshold) || 0,
        alert_percentage: parseFloat(formData.alert_percentage) || 0,
        approved_by: formData.approved_by,
        approval_date: formData.approval_date,
        responsible_person: formData.responsible_person,
        cost_center: formData.cost_center,
        project_code: formData.project_code,
        variance_amount: parseFloat(formData.variance_amount) || 0,
        variance_percentage: parseFloat(formData.variance_percentage) || 0,
        notes: formData.notes,
        justification: formData.justification
      };
      if (editingBudget) {
        await localApiAdapter.put(`/achats/budget/${editingBudget.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/budget', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('empty_dash') }));
    } finally {
      resetForm();
      loadBudgets();
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({ 
      budget_reference: budget.budget_reference || '',
      budget_name: budget.budget_name || '',
      fiscal_year: budget.fiscal_year || '',
      period_start: budget.period_start || '',
      period_end: budget.period_end || '',
      budget_type: budget.budget_type || 'annual',
      status: budget.status || 'draft',
      department: budget.department || '',
      category: budget.category || '',
      priority: budget.priority || 'medium',
      allocated_amount: budget.allocated_amount?.toString() || '',
      spent_amount: budget.spent_amount?.toString() || '',
      committed_amount: budget.committed_amount?.toString() || '',
      available_amount: budget.available_amount?.toString() || '',
      budget_threshold: budget.budget_threshold?.toString() || '',
      alert_percentage: budget.alert_percentage?.toString() || '',
      approved_by: budget.approved_by || '',
      approval_date: budget.approval_date || '',
      responsible_person: budget.responsible_person || '',
      cost_center: budget.cost_center || '',
      project_code: budget.project_code || '',
      variance_amount: budget.variance_amount?.toString() || '',
      variance_percentage: budget.variance_percentage?.toString() || '',
      notes: budget.notes || '',
      justification: budget.justification || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/budget/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadBudgets();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedBudgets.length }))) {
      try {
        const deleteCount = selectedBudgets.length;
        await Promise.all(selectedBudgets.map(id => localApiAdapter.delete(`/achats/budget/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedBudgets([]);
        loadBudgets();
      } catch (error) {
        console.error('Error deleting budgets:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('empty_dash') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      budget_reference: '', 
      budget_name: '',
      fiscal_year: '',
      period_start: '',
      period_end: '',
      budget_type: 'annual',
      status: 'draft',
      department: '',
      category: '',
      priority: 'medium',
      allocated_amount: '',
      spent_amount: '',
      committed_amount: '',
      available_amount: '',
      budget_threshold: '',
      alert_percentage: '',
      approved_by: '',
      approval_date: '',
      responsible_person: '',
      cost_center: '',
      project_code: '',
      variance_amount: '',
      variance_percentage: '',
      notes: '',
      justification: ''
    });
    setEditingBudget(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'budget_reference', label: p('export_col_reference') },
      { key: 'budget_name', label: p('export_col_name') },
      { key: 'budget_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'allocated_amount', label: p('col_allocated') },
      { key: 'spent_amount', label: p('col_spent') },
      { key: 'available_amount', label: p('col_available') }
    ];
    const fileName = `budget_achats_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredBudgets, headers, `${fileName}.xlsx`);
    setShowExportDropdown(false);
  };

  const handleExportCSV = () => {
    const headers = [
      { key: 'budget_reference', label: p('export_col_reference') },
      { key: 'budget_name', label: p('export_col_name') },
      { key: 'budget_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'allocated_amount', label: p('col_allocated') },
    ];
    const fileName = `budget_achats_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(filteredBudgets, headers, `${fileName}.csv`);
    setShowExportDropdown(false);
  };

  const handleImport = () => {
    const headers = [
      { key: 'budget_reference', label: p('export_col_reference') },
      { key: 'budget_name', label: p('export_col_name') },
      { key: 'budget_type', label: p('export_col_type') },
      { key: 'status', label: p('export_col_status') },
      { key: 'allocated_amount', label: p('col_allocated') }
    ];
    const schema = {
      requiredFields: ['budget_reference', 'budget_name'],
      validators: {
        allocated_amount: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/budget', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadBudgets();
          alert(p('alert_import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedBudgets([]);
    } else {
      setSelectedBudgets(filteredBudgets.map((r) => r.id));
    }
  };
  
  const handleSelectBudget = (id) => { 
    setSelectedBudgets(selectedBudgets.includes(id) ? selectedBudgets.filter(bId => bId !== id) : [...selectedBudgets, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/budget/${recordId}`, { status: newStatus });
      await loadBudgets();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const budgetTypeOptions = useMemo(() => ([
    { value: 'annual', label: p('type_annual') },
    { value: 'quarterly', label: p('type_quarterly') },
    { value: 'monthly', label: p('type_monthly') },
    { value: 'project', label: p('type_by_project') },
    { value: 'department', label: p('type_by_department') }
  ]), [p]);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'approved', label: p('status_approved'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'active', label: p('status_active'), icon: Shield, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'closed', label: p('status_closed'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: AlertCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' }
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') }
  ]), [p]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('budget_reference'),
      textColumnRule('budget_name'),
      exactColumnRule('budget_type', (b) => b.budget_type || ''),
      textColumnRule('period_start', (b) => (b.period_start ? new Date(b.period_start).toLocaleDateString(dateLocale) : '')),
      textColumnRule('allocated_amount', (b) => String(b.allocated_amount ?? '')),
      textColumnRule('spent_amount', (b) => String(b.spent_amount ?? '')),
      textColumnRule('utilization', (b) => {
        const allocated = parseFloat(b.allocated_amount) || 0;
        const spent = parseFloat(b.spent_amount) || 0;
        return allocated > 0 ? String((spent / allocated * 100).toFixed(1)) : '0';
      }),
      exactColumnRule('priority', (b) => b.priority || ''),
      exactColumnRule('status', (b) => b.status || ''),
    ],
    [dateLocale],
  );

  const { filtered: filteredBudgets, displayed: displayedBudgets } = useAchatsListDisplay(
    baseFilteredBudgets,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedBudgets, filteredBudgets);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const categoryOptions = useMemo(() => ([
    { value: 'matières_premières', label: p('category_raw_materials') },
    { value: 'équipements', label: p('category_equipment') },
    { value: 'services', label: p('category_services') },
    { value: 'fournitures', label: p('category_supplies') }
  ]), [p]);

  const kpiStats = useMemo(() => ([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: TrendingUp, variant: 'copper' },
    { key: 'draft', label: p('dist_draft'), value: stats.draft, icon: FileText, variant: 'forest' },
    { key: 'approved', label: p('dist_approved'), value: stats.approved, icon: CheckCircle, variant: 'copper' },
    { key: 'active', label: p('dist_active'), value: stats.active, icon: Target, variant: 'forest' },
  ]), [stats, p]);

  const listStats = useMemo(() => mapAchatsStatCards(kpiStats), [kpiStats]);

  const budgetColumnKeys = useMemo(
    () => ['budget_reference', 'budget_name', 'budget_type', 'period_start', 'allocated_amount', 'spent_amount', 'utilization', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(budgetColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'budget_reference', labelKey: 'col_reference', required: true },
      { key: 'budget_name', labelKey: 'col_name' },
      { key: 'budget_type', labelKey: 'label_type' },
      { key: 'period_start', labelKey: 'label_period' },
      { key: 'allocated_amount', labelKey: 'col_allocated' },
      { key: 'spent_amount', labelKey: 'export_col_spent' },
      { key: 'utilization', labelKey: 'col_utilization' },
      { key: 'priority', labelKey: 'label_priority' },
      { key: 'status', labelKey: 'label_status' },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      importLabel: t('common.import'),
      exportLabel: t('common.export'),
      generatingLabel: t('common.loading'),
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExport,
      },
    }),
    [showImportDropdown, showExportDropdown, t],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
                setActiveDistributionFilter(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mainViewMode === 'all_budgets' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button
              onClick={() => setActiveDistributionFilter(null)}
              className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
            >
              <XCircle size={14} />
              {p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>
          {distributionInfo.data.map((item) => (
            <button
              key={item.filterKey}
              onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
              className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                activeDistributionFilter === item.filterKey
                  ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}
              title={p('filter_click_title', { name: item.name })}
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>
                  {item.name}
                </h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div
                  className={`h-3 rounded-full ${item.color} transition-all`}
                  style={{
                    width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%`,
                  }}
                />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
                {(() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  return p('pct_of_budgets', { pct });
                })()}
              </p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enableBudgetLink ? p('notice_budget_disabled', { defaultValue: 'ربط بالميزانية معطّل — فعّله من إعدادات المشتريات ← ربط بالميزانية.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={TrendingUp}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedBudgets.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedBudgets.length})`, title: p('bulk_delete_confirm', { count: selectedBudgets.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={() => {
          if (!achatsConfig.enableBudgetLink) {
            alert(p('feature_disabled', { defaultValue: 'ربط بالميزانية معطّل من إعدادات المشتريات.' }));
            return;
          }
          setEditingBudget(null);
          setFormData({
            budget_reference: generateBudgetReference(),
            budget_name: '',
            fiscal_year: String(new Date().getFullYear()),
            period_start: '',
            period_end: '',
            budget_type: 'annual',
            status: 'draft',
            department: '',
            category: '',
            priority: 'medium',
            allocated_amount: '',
            spent_amount: '',
            committed_amount: '',
            available_amount: '',
            budget_threshold: String(achatsConfig.budgetAlertThreshold ?? 80),
            alert_percentage: String(achatsConfig.budgetAlertThreshold ?? 80),
            approved_by: '',
            approval_date: '',
            responsible_person: '',
            cost_center: '',
            project_code: '',
            variance_amount: '',
            variance_percentage: '',
            notes: '',
            justification: '',
          });
          setFormTab('general');
          setShowModal(true);
        }}
        createLabel={p('new_item')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search') })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>

            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('budget_reference') ? <TableColumnHeaderFilter {...colHeader('budget_reference', p('col_reference'))} /> : null}
                  {isColumnVisible('budget_name') ? <TableColumnHeaderFilter {...colHeader('budget_name', p('col_name'))} /> : null}
                  {isColumnVisible('budget_type') ? <TableColumnHeaderFilter {...colHeader('budget_type', p('label_type'), { kind: 'select', options: budgetTypeOptions })} /> : null}
                  {isColumnVisible('period_start') ? <TableColumnHeaderFilter {...colHeader('period_start', p('label_period'))} /> : null}
                  {isColumnVisible('allocated_amount') ? <TableColumnHeaderFilter {...colHeader('allocated_amount', p('col_allocated'), { align: 'center' })} /> : null}
                  {isColumnVisible('spent_amount') ? <TableColumnHeaderFilter {...colHeader('spent_amount', p('export_col_spent'), { align: 'center' })} /> : null}
                  {isColumnVisible('utilization') ? <TableColumnHeaderFilter {...colHeader('utilization', p('col_utilization'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('label_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('label_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedBudgets.length > 0 ? (
                  displayedBudgets.map((budget, index) => {
                    const allocated = parseFloat(budget.allocated_amount) || 0;
                    const spent = parseFloat(budget.spent_amount) || 0;
                    const utilization = allocated > 0 ? (spent / allocated * 100).toFixed(1) : 0;
                    
                    return (
                      <tr 
                        key={budget.id} 
                        className={`hover:bg-white/5 transition-colors ${selectedBudgets.includes(budget.id) ? 'bg-white/10' : ''}`}
                      >
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedBudgets.includes(budget.id)}
                              onChange={() => handleSelectBudget(budget.id)}
                            />
                          </div>
                        </td>
                        {isColumnVisible('budget_reference') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-semibold">{budget.budget_reference}</span>
                        </td>
                        ) : null}
                        {isColumnVisible('budget_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{budget.budget_name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{budget.department || '-'}</span>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('budget_type') ? (
                        <td className="px-6 py-4">
                          <span className="text-white/80">{budgetTypeOptions.find(t => t.value === budget.budget_type)?.label || budget.budget_type}</span>
                        </td>
                        ) : null}
                        {isColumnVisible('period_start') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{budget.period_start ? new Date(budget.period_start).toLocaleDateString(dateLocale) : '-'}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('label_end')}: {budget.period_end ? new Date(budget.period_end).toLocaleDateString(dateLocale) : '-'}</span>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('allocated_amount') ? (
                        <td className="px-6 py-4 text-right">
                          <span className="text-blue-300 font-semibold">{formatBrandMoney(allocated)}</span>
                        </td>
                        ) : null}
                        {isColumnVisible('spent_amount') ? (
                        <td className="px-6 py-4 text-right">
                          <span className="text-orange-300 font-semibold">{formatBrandMoney(spent)}</span>
                        </td>
                        ) : null}
                        {isColumnVisible('utilization') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col items-center">
                            <span className={`font-semibold ${utilization > 90 ? 'text-red-300' : utilization > 75 ? 'text-orange-300' : 'text-green-300'}`}>
                              {utilization}%
                            </span>
                            <div className="w-20 bg-white/10 h-2 rounded-full overflow-hidden mt-1">
                              <div 
                                className={`h-2 rounded-full ${utilization > 90 ? 'bg-red-500' : utilization > 75 ? 'bg-orange-500' : 'bg-green-500'}`} 
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('priority') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">{getPriorityBadge(budget.priority)}</div>
                        </td>
                        ) : null}
                        {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={budget.status} recordId={budget.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          </div>
                        </td>
                        ) : null}
                        <td className="px-6 py-4">
                          <QawaemErpRowActions
                            onView={() => { setViewingBudget(budget); setShowViewModal(true); }}
                            onEdit={() => handleEdit(budget)}
                            onDelete={() => handleDelete(budget.id)}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <TrendingUp size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View - Condensed for length */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedBudgets.length > 0 ? (
              displayedBudgets.map((budget, index) => {
                const allocated = parseFloat(budget.allocated_amount) || 0;
                const spent = parseFloat(budget.spent_amount) || 0;
                const available = parseFloat(budget.available_amount) || 0;
                const utilization = allocated > 0 ? (spent / allocated * 100).toFixed(1) : 0;
                
                return (
                  <motion.div
                    key={budget.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-4">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Wallet size={20} className="text-blue-400" />
                            {budget.budget_name}
                          </h3>
                          <div className="flex gap-2">
                            <QawaemClickableStatusBadge status={budget.status} recordId={budget.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                            {getPriorityBadge(budget.priority)}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('label_reference')}:</span> {budget.budget_reference}</div>
                              <div><span className="font-medium">{p('label_type')}:</span> {budgetTypeOptions.find(t => t.value === budget.budget_type)?.label}</div>
                              <div><span className="font-medium">{p('label_department')}:</span> {budget.department || '-'}</div>
                            </div>
                          </div>
                          
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('label_period')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('label_start')}:</span> {budget.period_start ? new Date(budget.period_start).toLocaleDateString(dateLocale) : '-'}</div>
                              <div><span className="font-medium">{p('label_end')}:</span> {budget.period_end ? new Date(budget.period_end).toLocaleDateString(dateLocale) : '-'}</div>
                              <div><span className="font-medium">{p('label_fiscal_year')}:</span> {budget.fiscal_year || '-'}</div>
                            </div>
                          </div>
                          
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('section_amounts')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('card_allocated')}:</span> <span className="text-blue-300">{formatBrandMoney(allocated)}</span></div>
                              <div><span className="font-medium">{p('card_spent')}:</span> <span className="text-orange-300">{formatBrandMoney(spent)}</span></div>
                              <div><span className="font-medium">{p('card_available')}:</span> <span className="text-green-300">{formatBrandMoney(available)}</span></div>
                            </div>
                          </div>
                          
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('col_utilization')}</h5>
                            <div className="flex flex-col items-start">
                              <span className={`text-2xl font-bold ${utilization > 90 ? 'text-red-300' : utilization > 75 ? 'text-orange-300' : 'text-green-300'}`}>
                                {utilization}%
                              </span>
                              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden mt-2">
                                <div 
                                  className={`h-3 rounded-full ${utilization > 90 ? 'bg-red-500' : utilization > 75 ? 'bg-orange-500' : 'bg-green-500'}`} 
                                  style={{ width: `${Math.min(utilization, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingBudget(budget); setShowViewModal(true); }}
                        onEdit={() => handleEdit(budget)}
                        onDelete={() => handleDelete(budget.id)}
                      />
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-12">
                <TrendingUp size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal and Edit Modal - Due to length limitations, I'll create a condensed version in a follow-up if needed */}
      {showViewModal && viewingBudget ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_reference')}</p>
                      <p className={m.viewValue}>{viewingBudget.budget_reference}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValue}>{viewingBudget.budget_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_type')}</p>
                      <p className={m.viewValue}>{budgetTypeOptions.find(t => t.value === viewingBudget.budget_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_status')}</p>
                      <div className="flex">{getStatusBadge(viewingBudget.status)}</div>
                    </div>
                  </div>
                </div>

                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_amounts')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('card_allocated')}</p>
                      <p className="text-blue-300 font-semibold">{formatBrandMoney(viewingBudget.allocated_amount)}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('card_spent')}</p>
                      <p className="text-orange-300 font-semibold">{formatBrandMoney(viewingBudget.spent_amount)}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('card_available')}</p>
                      <p className="text-green-300 font-semibold">{formatBrandMoney(viewingBudget.available_amount)}</p>
                    </div>
                  </div>
                </div>

                {viewingBudget.notes && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_notes')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingBudget.notes}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - Continuing with form sections */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingBudget ? p('modal_edit') : p('modal_create')}
        icon={DollarSign}
        formId="budget-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingBudget ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="budget-form" onSubmit={handleSubmit}>
              {formTab === 'general' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_budget_reference')} required /></label>
                    <input
                      type="text"
                      required
                      value={formData.budget_reference}
                      onChange={(e) => setFormData({ ...formData, budget_reference: e.target.value })}
                      className={`${cfgTheme.input} font-mono`}
                      placeholder={p('placeholder_budget_reference')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('label_budget_name')} required /></label>
                    <input
                      type="text"
                      required
                      value={formData.budget_name}
                      onChange={(e) => setFormData({ ...formData, budget_name: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_budget_name')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_fiscal_year')}</label>
                    <input
                      type="text"
                      value={formData.fiscal_year}
                      onChange={(e) => setFormData({ ...formData, fiscal_year: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_fiscal_year')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_period_start')}</label>
                    <CustomDatePicker
                      value={formData.period_start}
                      onChange={(date) => setFormData({ ...formData, period_start: date })}
                      placeholder={p('placeholder_start_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_period_end')}</label>
                    <CustomDatePicker
                      value={formData.period_end}
                      onChange={(date) => setFormData({ ...formData, period_end: date })}
                      placeholder={p('placeholder_end_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_budget_type')}</label>
                    <CustomSelect
                      value={formData.budget_type}
                      onChange={(val) => setFormData({ ...formData, budget_type: val || 'annual' })}
                      options={budgetTypeOptions}
                      placeholder={p('label_budget_type')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(val) => setFormData({ ...formData, status: val || 'draft' })}
                      options={statusOptions}
                      placeholder={p('label_status')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_department')}</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_department')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_category')}</label>
                    <CustomSelect
                      value={formData.category}
                      onChange={(val) => setFormData({ ...formData, category: val || '' })}
                      options={categoryOptions}
                      placeholder={p('label_category')}
                      searchable
                      creatable
                      autoSelect={false}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_priority')}</label>
                    <CustomSelect
                      value={formData.priority}
                      onChange={(val) => setFormData({ ...formData, priority: val || 'medium' })}
                      options={priorityOptions}
                      placeholder={p('label_priority')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                </div>
              )}

              {formTab === 'financial' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}>{p('label_allocated_amount', { suffix: currencySuffix })}</label>
                    <CustomNumberInput
                      value={formData.allocated_amount}
                      onChange={(value) => setFormData({ ...formData, allocated_amount: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_spent_amount', { suffix: currencySuffix })}</label>
                    <CustomNumberInput
                      value={formData.spent_amount}
                      onChange={(value) => setFormData({ ...formData, spent_amount: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_committed_amount', { suffix: currencySuffix })}</label>
                    <CustomNumberInput
                      value={formData.committed_amount}
                      onChange={(value) => setFormData({ ...formData, committed_amount: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_available_amount', { suffix: currencySuffix })}</label>
                    <CustomNumberInput
                      value={formData.available_amount}
                      onChange={(value) => setFormData({ ...formData, available_amount: value })}
                      placeholder="0.00"
                      step={1000}
                      min={0}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_variance_amount', { suffix: currencySuffix })}</label>
                    <CustomNumberInput
                      value={formData.variance_amount}
                      onChange={(value) => setFormData({ ...formData, variance_amount: value })}
                      placeholder="0.00"
                      step={1000}
                      decimals={2}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_variance_percentage')}</label>
                    <CustomNumberInput
                      value={formData.variance_percentage}
                      onChange={(value) => setFormData({ ...formData, variance_percentage: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                      decimals={1}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_budget_threshold')}</label>
                    <CustomNumberInput
                      value={formData.budget_threshold}
                      onChange={(value) => setFormData({ ...formData, budget_threshold: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                      max={100}
                      decimals={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_alert_percentage')}</label>
                    <CustomNumberInput
                      value={formData.alert_percentage}
                      onChange={(value) => setFormData({ ...formData, alert_percentage: value })}
                      placeholder="0"
                      step={1}
                      min={0}
                      max={100}
                      decimals={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_approved_by')}</label>
                    <input
                      type="text"
                      value={formData.approved_by}
                      onChange={(e) => setFormData({ ...formData, approved_by: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_approved_by')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_approval_date')}</label>
                    <CustomDatePicker
                      value={formData.approval_date}
                      onChange={(date) => setFormData({ ...formData, approval_date: date })}
                      placeholder={p('placeholder_select_date')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_responsible')}</label>
                    <input
                      type="text"
                      value={formData.responsible_person}
                      onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_responsible')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_cost_center')}</label>
                    <input
                      type="text"
                      value={formData.cost_center}
                      onChange={(e) => setFormData({ ...formData, cost_center: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_cost_center')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_project_code')}</label>
                    <input
                      type="text"
                      value={formData.project_code}
                      onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_project_code')}
                    />
                  </div>
                </div>
              )}

              {formTab === 'notes' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_justification')}</label>
                    <textarea
                      value={formData.justification}
                      onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                      placeholder={p('placeholder_justification')}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('section_notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[150px]"
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </div>
              )}

              </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default BudgetAchatsPage;

